import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Pressable } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useOwnerScope } from '../../../src/context/OwnerScopeContext';
import { useToast } from '../../../src/components/Toast';
import Card from '../../../src/components/Card';
import Input from '../../../src/components/Input';
import Button from '../../../src/components/Button';
import Picker from '../../../src/components/Picker';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import { titleCase } from '../../../src/utils/format';

interface SearchResult {
  id: string;
  name: string;
  accountNumber?: string;
  isAttendant: boolean;
}

interface Location {
  id: string;
  name: string;
}

interface AttendantRole {
  id: string;
  name: string;
  permissions: Record<string, unknown>;
  sortOrder: number;
}

/** AddAttendantScreen - search users and assign as location attendants. */
export default function AddAttendantScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SearchResult | null>(null);

  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [role, setRole] = useState('junior');
  const [roleId, setRoleId] = useState('');
  const [roles, setRoles] = useState<AttendantRole[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const router = useRouter();
  const toast = useToast();
  const scope = useOwnerScope();

  useFocusEffect(useCallback(() => {
    fetchLocations();
    fetchRoles();
  }, []));

  const fetchLocations = async () => {
    try {
      const data = await scope.api.get<Location[]>('/api/locations');
      setLocations(data);
      if (data.length === 1) {
        setSelectedLocationIds([data[0].id]);
      }
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const fetchRoles = async () => {
    try {
      const data = await scope.api.get<AttendantRole[]>('/api/attendant-roles');
      setRoles(data);
      if (data.length > 0) setRoleId(data[0].id);
    } catch {
      // roles not available, use legacy picker
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const results = await scope.api.get<SearchResult[]>(`/api/attendants/search-customers?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(results);
      if (results.length === 0) {
        toast.show({ type: 'info', title: 'No Results', message: 'No users found matching your search' });
      }
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Search Failed', message: err.message });
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectUser = (user: SearchResult) => {
    if (user.isAttendant) {
      toast.show({
        type: 'warning',
        title: 'Already Attendant',
        message: 'This user is already an attendant. You can add them to more locations from their detail page.'
      });
      return;
    }
    setSelectedUser(user);
    setSearchResults([]);
    setSearchQuery('');
  };

  const toggleLocation = (locationId: string) => {
    setSelectedLocationIds(prev =>
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  const handleSubmit = async () => {
    if (!selectedUser) {
      toast.show({ type: 'error', title: 'Error', message: 'Please select a user first' });
      return;
    }

    if (selectedLocationIds.length === 0) {
      toast.show({ type: 'error', title: 'Error', message: 'Please select at least one location' });
      return;
    }

    setSubmitting(true);
    try {
      await scope.api.post('/api/attendants', {
        userId: selectedUser.id,
        locationIds: selectedLocationIds,
        role,
        ...(roleId ? { roleId } : {}),
      });

      toast.show({ type: 'success', title: 'Success', message: 'Attendant added successfully' });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedUser(null);
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      <ContentContainer>
      <Card>
        <Text style={styles.sectionTitle}>Search for User</Text>
        <Text style={styles.sectionDescription}>
          Enter the exact email, phone number, or account # of the user you want to add as an attendant
        </Text>

        <View style={styles.searchRow}>
          <Input
            placeholder="Enter exact email, phone, or account #"
            value={searchQuery}
            onChangeText={setSearchQuery}
            onSubmitEditing={handleSearch}
            returnKeyType="search"
            autoCapitalize="none"
            style={styles.searchInput}
            editable={!selectedUser}
          />
          <Button
            title="Search"
            onPress={handleSearch}
            loading={searching}
            disabled={!searchQuery.trim() || !!selectedUser}
            size="md"
            style={styles.searchButton}
          />
        </View>

        {searchResults.length > 0 && (
          <View style={styles.resultsContainer}>
            <Text style={styles.resultsLabel}>Search Results:</Text>
            {searchResults.map(user => (
              <TouchableOpacity
                key={user.id}
                style={styles.resultItem}
                onPress={() => handleSelectUser(user)}
              >
                <View style={styles.resultIcon}>
                  <Ionicons name="person-circle" size={32} color={colors.primary} />
                </View>
                <View style={styles.resultInfo}>
                  <View style={styles.resultNameRow}>
                    <Text style={styles.resultName}>{titleCase(user.name)}</Text>
                    {user.isAttendant && (
                      <View style={styles.attendantBadge}>
                        <Text style={styles.attendantBadgeText}>Already Attendant</Text>
                      </View>
                    )}
                  </View>
                  {user.accountNumber && <Text style={styles.resultEmail}>#{user.accountNumber}</Text>}
                </View>
                <Ionicons name="add-circle" size={24} color={user.isAttendant ? colors.textLight : colors.primary} />
              </TouchableOpacity>
            ))}
          </View>
        )}

        {selectedUser && (
          <View style={styles.selectedContainer}>
            <View style={styles.selectedHeader}>
              <Text style={styles.selectedLabel}>Selected User:</Text>
              <TouchableOpacity onPress={handleCancel}>
                <Ionicons name="close-circle" size={24} color={colors.danger} />
              </TouchableOpacity>
            </View>
            <View style={styles.selectedUser}>
              <Ionicons name="person-circle" size={40} color={colors.primary} />
              <View style={styles.selectedUserInfo}>
                <Text style={styles.selectedUserName}>{titleCase(selectedUser.name)}</Text>
                {selectedUser.accountNumber && <Text style={styles.selectedUserEmail}>#{selectedUser.accountNumber}</Text>}
              </View>
            </View>
          </View>
        )}
      </Card>

      {selectedUser && (
        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Attendant Details</Text>

          <Text style={styles.formLabel}>Assign to Locations</Text>
          <View style={styles.locationCheckboxes}>
            {locations.map(loc => {
              const isSelected = selectedLocationIds.includes(loc.id);
              return (
                <Pressable
                  key={loc.id}
                  style={[styles.checkboxRow, isSelected && styles.checkboxRowSelected]}
                  onPress={() => toggleLocation(loc.id)}
                >
                  <View style={[styles.checkbox, isSelected && styles.checkboxChecked]}>
                    {isSelected && <Ionicons name="checkmark" size={16} color={colors.white} />}
                  </View>
                  <Text style={[styles.checkboxLabel, isSelected && styles.checkboxLabelSelected]}>
                    {loc.name}
                  </Text>
                </Pressable>
              );
            })}
          </View>
          {selectedLocationIds.length === 0 && (
            <Text style={styles.checkboxHint}>Select at least one location</Text>
          )}

          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>Role</Text>
            {roles.length > 0 ? (
              <Picker
                options={roles.map(r => ({ label: r.name, value: r.id }))}
                value={roleId}
                onValueChange={(val: string) => {
                  setRoleId(val);
                  setRole('junior'); // fallback for legacy column
                }}
              />
            ) : (
              <Picker
                options={[
                  { label: 'Junior Attendant', value: 'junior' },
                  { label: 'Senior Attendant', value: 'senior' },
                  { label: 'Lead Attendant', value: 'lead' },
                ]}
                value={role}
                onValueChange={setRole}
              />
            )}
          </View>

          <View style={styles.buttonRow}>
            <Button
              title="Cancel"
              onPress={() => router.back()}
              variant="outline"
              style={styles.cancelButton}
            />
            <Button
              title="Add Attendant"
              onPress={handleSubmit}
              loading={submitting}
              disabled={!selectedUser || selectedLocationIds.length === 0}
              style={styles.submitButton}
            />
          </View>
        </Card>
      )}

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: c.background,
  },
  scrollContent: {
    padding: spacing.md,
  },
  sectionTitle: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.bold,
    color: c.text,
    marginBottom: spacing.xs,
  },
  sectionDescription: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    marginBottom: spacing.md,
  },
  searchRow: {
    flexDirection: 'column',
    gap: spacing.sm,
  },
  searchInput: {
  },
  searchButton: {
    marginBottom: spacing.md,
  },
  resultsContainer: {
    marginTop: spacing.md,
  },
  resultsLabel: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.semibold,
    color: c.textSecondary,
    marginBottom: spacing.sm,
  },
  resultItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: c.surface,
    borderRadius: borderRadius.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: c.border,
  },
  resultIcon: {
    marginRight: spacing.md,
  },
  resultInfo: {
    flex: 1,
  },
  resultNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  resultName: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
  },
  attendantBadge: {
    backgroundColor: c.warning + '20',
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: borderRadius.sm,
  },
  attendantBadgeText: {
    fontSize: fontSize.xs,
    color: c.warning,
    fontWeight: fontWeight.medium,
  },
  resultEmail: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    marginTop: 2,
  },
  resultPhone: {
    fontSize: fontSize.sm,
    color: c.textLight,
    marginTop: 2,
  },
  selectedContainer: {
    marginTop: spacing.lg,
    padding: spacing.md,
    backgroundColor: c.primaryBg,
    borderRadius: borderRadius.md,
  },
  selectedHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  selectedLabel: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.semibold,
    color: c.primary,
  },
  selectedUser: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  selectedUserInfo: {
    flex: 1,
  },
  selectedUserName: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.bold,
    color: c.text,
  },
  selectedUserEmail: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    marginTop: 2,
  },
  formCard: {
    marginTop: spacing.md,
  },
  formGroup: {
    marginBottom: spacing.md,
  },
  formLabel: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    color: c.text,
    marginBottom: spacing.sm,
  },
  locationCheckboxes: {
    marginBottom: spacing.md,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: c.surface,
    borderRadius: borderRadius.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: c.border,
  },
  checkboxRowSelected: {
    borderColor: c.primary,
    backgroundColor: c.primaryBg,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: c.textLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  checkboxChecked: {
    backgroundColor: c.primary,
    borderColor: c.primary,
  },
  checkboxLabel: {
    fontSize: fontSize.md,
    color: c.text,
  },
  checkboxLabelSelected: {
    fontWeight: fontWeight.semibold,
    color: c.primary,
  },
  checkboxHint: {
    fontSize: fontSize.xs,
    color: c.danger,
    marginBottom: spacing.md,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
  cancelButton: {
    flex: 1,
  },
  submitButton: {
    flex: 1,
  },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
